/**
 * Maps Medicaid coverage into the read-only {@link MedicaidCoverageInformation}
 * display model when the tier's payer is Medicaid / Medicaid NYCSC.
 *
 * @module coverageInfo/helpers/mapMedicaidCoverageInformation
 */

import type {
  CoverageServiceCode,
  ManagedCareInformation,
  MedicaidCoverageInformation,
  ThirdPartyPayerInformation,
} from "../../../data-model/coverageInformation";
import type { CoverageResult } from "../../../data-model/medicalPaOrdersModel";

/** Placeholder rendered when a Medicaid field is missing or empty. */
const EMPTY_DISPLAY = "-";

/**
 * Normalized payer names that should render the Medicaid EV screen.
 * Matching is case-insensitive after trimming and stripping non-alphanumerics
 * so variants like "Medicaid NYCSC", "medicaidnycsc", and "Medicaid" all match.
 */
const MEDICAID_PAYER_KEYS = new Set(["medicaid", "medicaidnycsc"]);

/**
 * Coerces an arbitrary value to a trimmed display string, falling back to `-`.
 *
 * @param value - Raw value from the medicaid_nycsc_info payload
 * @returns Non-empty display string, or `-` when missing
 */
const toDisplayString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return EMPTY_DISPLAY;
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? EMPTY_DISPLAY : trimmed;
};

/**
 * Maps a raw `covered_services` / `non_covered_services` array into service-code rows.
 *
 * @param value - Raw array of `{ code, description }` entries (untyped JSON)
 * @returns Parsed service-code rows (empty when input is not an array)
 */
const toServiceCodes = (value: unknown): CoverageServiceCode[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    return {
      code: toDisplayString(row.code),
      description: toDisplayString(row.description),
    };
  });
};

/**
 * Builds the Third Party Payer (TPL) block from `tpl_*` fields.
 *
 * @param info - The medicaid_nycsc_info record (may be empty)
 * @returns Third Party Payer display block
 */
const buildThirdPartyPayer = (
  info: Record<string, unknown>,
): ThirdPartyPayerInformation => ({
  payerName: toDisplayString(info.tpl_payer_name),
  carrierCode: toDisplayString(info.tpl_carrier_code),
  address: toDisplayString(info.tpl_address),
  phone: toDisplayString(info.tpl_phone),
  policyNumber: toDisplayString(info.tpl_policy_number),
  groupNumber: toDisplayString(info.tpl_group_number),
});

/**
 * Builds the Managed Medicaid Care (MMC) block from `managed_care_*` fields.
 *
 * @param info - The medicaid_nycsc_info record (may be empty)
 * @returns Managed care display block
 */
const buildManagedCare = (
  info: Record<string, unknown>,
): ManagedCareInformation => ({
  planName: toDisplayString(info.managed_care_plan_name),
  planCode: toDisplayString(info.managed_care_plan_code),
  phone: toDisplayString(info.managed_care_phone),
  address: toDisplayString(info.managed_care_address),
});

/**
 * Whether the given payer name should show the Medicaid EV screen.
 *
 * @param payerName - Raw `coverage.payer_name` value
 * @returns `true` when the payer is Medicaid or Medicaid NYCSC
 */
export const isMedicaidPayerName = (payerName: string | undefined): boolean => {
  const normalized = (payerName ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, "");
  return MEDICAID_PAYER_KEYS.has(normalized);
};

/**
 * Whether a value is a real Medicaid NYCSC postprocessor dump.
 *
 * The RPA postprocessor always writes `MedicaidNycscInfo.model_dump()` with
 * `covered_services` and `non_covered_services` as arrays (possibly empty).
 * Requiring those arrays distinguishes usable detail from a missing or empty
 * `medicaid_nycsc_info` payload.
 *
 * @param value - Raw `processing_metadata.medicaid_nycsc_info` value
 * @returns `true` when the value matches the Medicaid NYCSC info shape
 */
const isMedicaidNycscInfo = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Array.isArray((value as Record<string, unknown>).covered_services) &&
  Array.isArray((value as Record<string, unknown>).non_covered_services);

/**
 * Extracts Medicaid coverage details when the tier's payer is Medicaid.
 *
 * Detection is by `payer_name` (`medicaid` or `medicaidnycsc`, case-insensitive).
 * When `processing_metadata.medicaid_nycsc_info` is present, service and payer
 * fields are mapped into the display model (`hasNycscDetails: true`). When the
 * metadata key is missing or malformed, `hasNycscDetails` is false so the UI
 * can show an overall empty/info state instead of empty tabs.
 *
 * @param payerName - `coverage.payer_name` for the active tier
 * @param coverageResult - The coverage_result row for the active tier
 * @returns Medicaid display model, or `undefined` when the tier is not Medicaid
 */
export const buildMedicaidCoverageInformation = (
  payerName: string | undefined,
  coverageResult?: CoverageResult,
): MedicaidCoverageInformation | undefined => {
  if (!isMedicaidPayerName(payerName)) {
    return undefined;
  }

  const processingMetadata = coverageResult?.processing_metadata as
    Record<string, unknown> | undefined;
  const info = processingMetadata?.medicaid_nycsc_info;
  if (!isMedicaidNycscInfo(info)) {
    return {
      hasNycscDetails: false,
      coveredServices: [],
      nonCoveredServices: [],
      thirdPartyPayer: buildThirdPartyPayer({}),
      managedCare: buildManagedCare({}),
    };
  }

  return {
    hasNycscDetails: true,
    coveredServices: toServiceCodes(info.covered_services),
    nonCoveredServices: toServiceCodes(info.non_covered_services),
    thirdPartyPayer: buildThirdPartyPayer(info),
    managedCare: buildManagedCare(info),
  };
};
